<template>
    <div
        class="user-form text-start flex-grow-1 d-flex flex-column"
        data-aos="fade-left"
        data-aos-duration="1500"
    >
        <v-container class="p-0 d-flex flex-column flex-grow-1" fluid>
            <div class="page-card flex-grow-1">
                <div class="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-2">
                    <div>
                        <h1 class="card-title mb-2">
                            <span v-if="!userId">{{ $t('admin.userForm.register') }}</span>
                            <span v-else>{{ $t('admin.userForm.edit') }}</span>
                            {{ $t('admin.userForm.userNoun') }}
                        </h1>
                        <p class="card-subtitle mb-0">
                            {{ userId ? $t('admin.userForm.editLead') : $t('admin.userForm.registerLead') }}
                        </p>
                    </div>
                    <v-btn
                        v-if="!userId"
                        variant="tonal"
                        color="primary"
                        class="text-none"
                        prepend-icon="mdi-auto-fix"
                        @click="fillDemoData"
                    >
                        {{ $t('admin.userForm.fillDemoData') }}
                    </v-btn>
                </div>

                <hr class="page-card__divider mt-4 mt-md-5" />
                <div v-if="isLoading" class="pa-10">
                    <Loader />
                </div>
                <v-form
                    v-else
                    fast-fail
                    @submit.prevent="handleSubmit()"
                    ref="userForm"
                >
                    <v-row class="mt-2 mt-md-4">
                        <v-col
                            cols="12"
                            md="7"
                            offset-lg="2"
                            class="d-md-flex justify-content-md-center py-1"
                        >
                            <img
                                class="user-photo"
                                :class="{ 'gray-scale': !user.photo }"
                                :src="profileImage"
                            />

                            <div class="ms-md-4 flex-fill">
                                <v-label class="labeld" for="userPhotoInput"
                                    >{{ $t('admin.userForm.photo') }}</v-label
                                >
                                <v-file-input
                                    id="userPhotoInput"
                                    v-model="user.photo"
                                    accept="image/*"
                                    class="inputd"
                                    variant="outlined"
                                    density="compact"
                                    :prepend-icon="false"
                                    prepend-inner-icon="mdi-camera"
                                    @change="onPhotoChange"
                                    @click:clear="clearPhoto"
                                ></v-file-input>
                            </div>
                        </v-col>
                        <v-col cols="12" sm="6" md="4" class="py-1">
                            <v-label class="labeld required" for="userNameInput"
                                >{{ $t('admin.userForm.name') }}</v-label
                            >
                            <v-text-field
                                id="userNameInput"
                                v-model="user.name"
                                :rules="rules.name"
                                :placeholder="$t('admin.userForm.namePlaceholder')"
                                required
                                class="inputd"
                                variant="outlined"
                                density="compact"
                            ></v-text-field>
                        </v-col>
                        <v-col cols="12" sm="6" md="4" class="py-1">
                            <v-label
                                for="userEmailInput"
                                class="labeld required"
                            >
                                Email
                            </v-label>
                            <v-text-field
                                id="userEmailInput"
                                v-model="user.email"
                                :rules="rules.email"
                                :placeholder="$t('admin.userForm.emailPlaceholder')"
                                required
                                type="email"
                                class="inputd"
                                variant="outlined"
                                density="compact"
                            ></v-text-field>
                        </v-col>
                        <v-col cols="12" sm="6" md="2" class="py-1">
                            <v-label
                                for="userTypeInput"
                                class="labeld required"
                            >
                                {{ $t('admin.users.type') }}
                            </v-label>
                            <v-select
                                id="userTypeInput"
                                v-model="user.type"
                                :items="userTypes"
                                item-title="text"
                                item-value="value"
                                :placeholder="$t('admin.userForm.userTypePlaceholder')"
                                class="inputd"
                                variant="outlined"
                                density="compact"
                            />
                        </v-col>
                        <v-col cols="12" sm="6" md="2" class="py-1">
                            <v-label
                                for="userStatusSelect"
                                class="labeld required"
                            >
                                {{ $t('common.status') }}
                            </v-label>
                            <v-select
                                id="userStatusSelect"
                                v-model="user.status"
                                :items="statusTypes"
                                item-title="text"
                                item-value="value"
                                :placeholder="$t('admin.userForm.statusPlaceholder')"
                                class="inputd"
                                variant="outlined"
                                density="compact"
                            />
                        </v-col>
                        <v-col cols="12" sm="6" md="4" class="py-1">
                            <v-label
                                class="labeld required"
                                for="userSocialNumber"
                                >{{ $t('admin.userForm.cpf') }}</v-label
                            >
                            <v-text-field
                                id="userSocialNumber"
                                :placeholder="$t('admin.userForm.cpfPlaceholder')"
                                v-model="user.socialNumber"
                                :rules="rules.socialNumber"
                                v-mask="'###.###.###-##'"
                                required
                                class="inputd"
                                variant="outlined"
                                density="compact"
                            />
                        </v-col>
                        <v-col cols="12" sm="6" md="4" class="py-1">
                            <v-label class="labeld" for="userIdNumber"
                                >{{ $t('admin.userForm.idNumber') }}</v-label
                            >
                            <v-text-field
                                id="userIdNumber"
                                :placeholder="$t('admin.userForm.idNumberPlaceholder')"
                                v-model="user.idNumber"
                                class="inputd"
                                variant="outlined"
                                density="compact"
                            />
                        </v-col>
                        <v-col cols="12" sm="6" md="4" class="py-1">
                            <v-label class="labeld" for="userPhoneInput"
                                >{{ $t('admin.userForm.phone') }}</v-label
                            >
                            <v-text-field
                                id="userPhoneInput"
                                v-model="user.phone"
                                type="tel"
                                :placeholder="$t('admin.userForm.phonePlaceholder')"
                                v-mask="'(##) #####-####'"
                                class="inputd"
                                variant="outlined"
                                density="compact"
                            />
                        </v-col>
                        <v-col cols="12" sm="3" md="2" class="py-1">
                            <v-label class="labeld" for="userPostCodeInput"
                                >{{ $t('admin.userForm.postcode') }}</v-label
                            >
                            <v-text-field
                                id="userPostCodeInput"
                                v-model="user.postcode"
                                placeholder="xxxxx-xxx"
                                v-mask="'#####-###'"
                                class="inputd"
                                variant="outlined"
                                density="compact"
                                @change="getCEP()"
                            />
                        </v-col>
                        <v-col cols="12" sm="6" md="6" class="py-1">
                            <v-label class="labeld" for="userAddressInput"
                                >{{ $t('admin.userForm.address') }}</v-label
                            >
                            <v-text-field
                                v-model="user.address"
                                id="userAddressInput"
                                :placeholder="$t('admin.userForm.addressPlaceholder')"
                                class="inputd"
                                variant="outlined"
                                density="compact"
                                :loading="isLoadingAddress"
                            />
                        </v-col>
                        <v-col cols="12" sm="3" md="2" class="py-1">
                            <v-label class="labeld" for="userAddressNumberInput"
                                >{{ $t('admin.userForm.number') }}</v-label
                            >
                            <v-text-field
                                id="userAddressNumberInput"
                                v-model="user.addressNumber"
                                :placeholder="$t('admin.userForm.numberPlaceholder')"
                                class="inputd"
                                variant="outlined"
                                density="compact"
                            />
                        </v-col>
                        <v-col cols="12" sm="12" md="2" class="py-1">
                            <v-label
                                class="labeld"
                                for="userAddressComplementInput"
                                >{{ $t('admin.userForm.complement') }}</v-label
                            >
                            <v-text-field
                                id="userAddressComplementInput"
                                v-model="user.addressComplement"
                                :placeholder="$t('admin.userForm.complement')"
                                class="inputd"
                                variant="outlined"
                                density="compact"
                                maxlength="100"
                            />
                        </v-col>
                        <v-col cols="12" sm="6" md="4" class="py-1">
                            <v-label class="labeld" for="userCityInput"
                                >{{ $t('admin.userForm.city') }}</v-label
                            >
                            <v-text-field
                                id="userCityInput"
                                v-model="user.city"
                                :placeholder="$t('admin.userForm.city')"
                                class="inputd"
                                variant="outlined"
                                density="compact"
                                maxlength="255"
                                :loading="isLoadingAddress"
                            />
                        </v-col>
                        <v-col cols="12" sm="3" md="4" class="py-1">
                            <v-label class="labeld" for="userStateInput"
                                >{{ $t('admin.userForm.state') }}</v-label
                            >
                            <v-select
                                id="userStateInput"
                                v-model="user.county"
                                :items="brazilianStates"
                                item-title="nome"
                                item-value="sigla"
                                :placeholder="$t('admin.userForm.statePlaceholder')"
                                class="inputd"
                                variant="outlined"
                                density="compact"
                                :loading="isLoadingAddress"
                            />
                        </v-col>
                        <v-col cols="12" sm="3" md="4" class="py-1">
                            <v-label class="labeld" for="userCountryInput"
                                >{{ $t('admin.userForm.country') }}</v-label
                            >
                            <v-select
                                id="userCountryInput"
                                v-model="user.country"
                                :items="countryOptions"
                                item-title="nome"
                                item-value="sigla"
                                class="inputd"
                                variant="outlined"
                                density="compact"
                            />
                        </v-col>
                        <v-col
                            v-if="userId"
                            cols="12"
                            sm="6"
                            md="4"
                            class="py-1"
                        >
                            <v-label class="labeld" for="userLocaleInput">{{
                                $t('common.language')
                            }}</v-label>
                            <v-select
                                id="userLocaleInput"
                                :model-value="currentLocale"
                                :items="localeOptions"
                                item-title="title"
                                item-value="value"
                                class="inputd"
                                variant="outlined"
                                density="compact"
                                @update:model-value="onLocaleChange"
                            />
                        </v-col>
                        
                        <!-- Nota sobre envio de email -->
                        <v-col cols="12" class="mt-4">
                            <small class="text-muted">
                                <i class="mdi mdi-email-alert me-1"></i>
                                <strong>{{ $t('common.info') }}:</strong> {{ $t('admin.userForm.activationNote') }}
                            </small>
                        </v-col>
                        
                        <v-col cols="12" class="mt-5 mt-md-6">
                            <v-row>
                                <v-col v-if="userId" cols="12" sm="6" md="6" class="d-flex gap-2">
                                    <v-btn
                                        color="danger"
                                        height="47"
                                        variant="elevated"
                                        class="text-white"
                                        :disabled="isLoading"
                                        @click="openDeleteDialog"
                                        >{{ $t('common.delete') }}</v-btn
                                    >
                                    <v-btn
                                        color="primary"
                                        height="47"
                                        variant="outlined"
                                        :loading="isResettingPassword"
                                        :disabled="isResettingPassword || isLoading"
                                        @click="resetPassword"
                                        class="reset-password-btn"
                                    >
                                        {{ $t('admin.userForm.resetPassword') }}
                                    </v-btn>
                                </v-col>
                                <v-col
                                    cols="12"
                                    :sm="userId ? 6 : 12"
                                    :md="userId ? 6 : 12"
                                    class="d-flex justify-content-center justify-content-md-end"
                                >
                                    <button
                                        class="btnd btnd--purple-transparent min-w-30 mr-4 mr-md-5"
                                        type="button"
                                        @click="$router.go(-1)"
                                    >
                                        {{ $t('common.back') }}
                                    </button>
                                    <v-btn
                                        class="btnd btnd--purple min-w-30"
                                        :height="47"
                                        type="submit"
                                    >
                                        {{ $t('common.save') }}
                                    </v-btn>
                                </v-col>
                            </v-row>
                        </v-col>
                    </v-row>
                </v-form>
            </div>
        </v-container>

        <v-dialog v-model="isDeleteDialogVisible" :max-width="600">
            <v-card class="modal-card">
                <v-card-title>
                    <h3 class="mb-0">
                        {{ $t('admin.users.deleteConfirmTitle') }}
                    </h3>
                </v-card-title>
                <v-card-subtitle>
                    {{ $t('admin.users.deleteConfirmSubtitle') }}
                </v-card-subtitle>
                <v-card-text>
                    <p class="mb-1">
                        {{ $t('admin.users.name') }}: <strong>{{ dataUserFetched?.name }}</strong>
                    </p>
                    <p class="mb-1">
                        {{ $t('admin.users.email') }}: <strong>{{ dataUserFetched?.email }}</strong>
                    </p>
                </v-card-text>
                <v-card-actions>
                    <v-btn
                        class="flex-grow-1 cancel-btn"
                        color="grey-darken-4"
                        variant="tonal"
                        @click="isDeleteDialogVisible = false"
                        >{{ $t('common.cancel') }}</v-btn
                    >
                    <v-btn
                        class="flex-grow-1 text-white"
                        color="danger"
                        variant="flat"
                        :loading="isLoading"
                        :disabled="isLoading"
                        @click="confirmDelete"
                        >{{ $t('common.delete') }}</v-btn
                    >
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
    import { mapState } from 'vuex';

    import {
        requiredRule,
        emailRule,
        minLengthRule,
        confirmPasswordRule,
    } from '@/utils/validations/index';
    import DefaultProfileImg from '@/assets/user.png';
    import apiCep from '@/utils/cep';
    import apiAWS from '@/utils/storage/aws';
    import apiUser from '@/utils/Weflix/user';
    import brazilianStates from '@/mocks/brazilianStates';

    import { toast } from 'vue3-toastify';
    import Loader from '@/components/Weflix/Loader';
    import { setAppLocale } from '@/i18n';

    export default {
        components: {
            Loader,
        },
        name: 'UserForm',
        data() {
            return {
                isLoading: false,
                isLoadingAddress: false,
                isResettingPassword: false,
                user: {
                    name: '',
                    email: '',
                    photo: '', // pode ser string (url) ou File
                    type: 3,
                    socialNumber: '',
                    idNumber: '',
                    phone: '',
                    postcode: '',
                    address: '',
                    addressNumber: '',
                    addressComplement: '',
                    neighborhood: '',
                    city: '',
                    county: 'AC',
                    country: 'br',
                    status: 1,
                },
                dataUserFetched: null,
                previewPhotoUrl: '',
                photoHasChanged: false,
                rules: {
                    name: [requiredRule],
                    email: [requiredRule, emailRule],
                    socialNumber: [requiredRule],
                },
                userTypes: [],
                statusTypes: [],
                brazilianStates: brazilianStates,
                isDeleteDialogVisible: false,
            };
        },
        computed: {
            ...mapState(['token']),
            userId() {
                return this.$route.params.id || null;
            },
            currentLocale() {
                const loc = this.$i18n.locale;
                return loc && typeof loc === 'object' && 'value' in loc
                    ? loc.value
                    : loc;
            },
            localeOptions() {
                return [
                    { title: this.$t('common.portuguese'), value: 'pt' },
                    { title: this.$t('common.english'), value: 'en' },
                    { title: this.$t('common.spanish'), value: 'es' },
                ];
            },
            profileImage() {
                if (this.previewPhotoUrl) return this.previewPhotoUrl;
                if (typeof this.user.photo === 'string' && this.user.photo)
                    return this.user.photo;
                return DefaultProfileImg;
            },
            countryOptions() {
                return [
                    { nome: this.$t('admin.userForm.countryBrazil'), sigla: 'br' },
                    { nome: this.$t('admin.userForm.countryOther'), sigla: 'outro' },
                ];
            },
            userTypes() {
                return [
                    { text: this.$t('admin.userForm.userTypeUser'), value: 3 },
                    { text: this.$t('admin.users.manager'), value: 2 },
                ];
            },
            statusTypes() {
                return [
                    { text: this.$t('common.active'), value: 1 },
                    { text: this.$t('common.inactive'), value: 0 },
                ];
            },
        },
        beforeMount() {
            if (this.userId) {
                this.getUserData();
            }
        },
        mounted() {
            // No password validation needed since password fields are removed
        },
        methods: {
            onLocaleChange(value) {
                if (value) setAppLocale(value);
            },
            /** Gera CPF válido (apenas dígitos) para cadastro de teste. */
            generateDemoCpf() {
                const nums = Array.from({ length: 9 }, () =>
                    Math.floor(Math.random() * 10),
                );
                const calcDigit = (base, factorStart) => {
                    let sum = 0;
                    for (let i = 0; i < base.length; i += 1) {
                        sum += base[i] * (factorStart - i);
                    }
                    const mod = (sum * 10) % 11;
                    return mod === 10 ? 0 : mod;
                };
                nums.push(calcDigit(nums, 10));
                nums.push(calcDigit(nums, 11));
                const digits = nums.join('');
                return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
            },
            fillDemoData() {
                const stamp = Date.now().toString(36).slice(-6);
                const tag = `aluno${stamp}`;
                const phoneDigits = `119${String(Math.floor(Math.random() * 1e8)).padStart(8, '0')}`;
                this.user = {
                    ...this.user,
                    name: `Samuel Linkon ${tag}`,
                    email: `samuel.linkon+${tag}@tutor.do`,
                    type: 3,
                    status: 1,
                    socialNumber: this.generateDemoCpf(),
                    idNumber: `MG${String(Math.floor(Math.random() * 1e7)).padStart(7, '0')}`,
                    phone: `(${phoneDigits.slice(0, 2)}) ${phoneDigits.slice(2, 7)}-${phoneDigits.slice(7)}`,
                    postcode: '01310-100',
                    address: 'Avenida Paulista',
                    addressNumber: String(100 + Math.floor(Math.random() * 900)),
                    addressComplement: 'Sala demo',
                    neighborhood: 'Bela Vista',
                    city: 'São Paulo',
                    county: 'SP',
                    country: 'br',
                };
                toast.success(this.$t('admin.userForm.fillDemoDataDone', { email: this.user.email }));
            },
        async resetPassword() {
            console.log(this.token);
          try {
            await apiUser.resetPassword(this.token, this.userId);
            toast.success(this.$t('admin.userForm.resetPasswordSuccess'));
          } catch (error) {
            toast.error(this.$t('admin.userForm.resetPasswordError'));
          }
        },
            onPhotoChange(event) {
                const file = event.target.files[0];
                this.updatePreview(file);
                this.photoHasChanged = true;
            },
            updatePreview(file) {
                // Limpa a url antiga se existir
                if (this.previewPhotoUrl) {
                    URL.revokeObjectURL(this.previewPhotoUrl);
                    this.previewPhotoUrl = '';
                }
                // Só cria url temporária se for File
                if (file instanceof File) {
                    this.previewPhotoUrl = URL.createObjectURL(file);
                } else {
                    this.previewPhotoUrl = '';
                    this.user.photo = '';
                }
            },
            clearPhoto() {
                this.previewPhotoUrl = '';
                this.user.photo = '';
            },
            openDeleteDialog() {
                if (this.dataUserFetched) {
                    this.isDeleteDialogVisible = true;
                }
            },
           
            async getUserData() {
                this.isLoading = true;
                this.isLoadingAddress = true;
                try {
                    const response = await apiUser.getUserId(
                        this.userId,
                        this.token
                    );
                    this.user = response;
                    this.dataUserFetched = response;
                    this.previewPhotoUrl = response.photoFinal;
                } catch (e) {
                    toast.error(this.$t('admin.userForm.fetchError'));
                } finally {
                    this.isLoading = false;
                    this.isLoadingAddress = false;
                }
            },
            async getCEP() {
                this.isLoadingAddress = true;
                try {
                    const response = await apiCep.getDataCep(
                        this.user.postcode
                    );
                    this.user.address = response.logradouro;
                    this.user.neighborhood = response.bairro;
                    this.user.city = response.localidade;
                    this.user.county = response.uf;
                } catch (error) {
                    toast.error(this.$t('admin.userForm.cepError'));
                } finally {
                    this.isLoadingAddress = false;
                }
            },
            async putFileAws(url, file) {
                try {
                    const response = await apiAWS.putFileAWS(url, file);
                    return response;
                } catch (error) {
                    toast.error(this.$t('admin.userForm.imageSaveError'));
                }
            },
            async saveImageAws(file) {
                try {
                    const response = await apiAWS.getSignatureImage(
                        this.token,
                        file.name
                    );
                    await this.putFileAws(response.url, file);
                    return response.token;
                } catch (error) {
                    toast.error(this.$t('admin.userForm.imageTokenError'));
                }
            },
            async handleSubmit() {
                this.isLoading = true;
                try {
                    if (
                        this.$refs.userForm.validate() &&
                        this.$refs.userForm.isValid
                    ) {
                        const payload = {
                            name: this.user.name,
                            email: this.user.email,
                            phone: this.user.phone.replace(/[^\d]/g, ''),
                            birthdate: '',
                            gender: '',
                            address: this.user.address,
                            addressComplement: this.user.addressComplement,
                            addressNumber: this.user.addressNumber,
                            neighborhood: this.user.neighborhood,
                            city: this.user.city,
                            county: this.user.county,
                            country: this.user.country,
                            postcode: this.user.postcode,
                            idNumber: this.user.idNumber,
                            socialNumber: this.user.socialNumber.replace(
                                /[^\d]/g,
                                ''
                            ),
                            status: this.user.status,
                            type: this.user.type,
                            photo: this.user.photo,
                        };

                        if (
                            this.photoHasChanged &&
                            this.user.photo instanceof File
                        ) {
                            payload.photo = await this.saveImageAws(
                                this.user.photo
                            );
                        }

                        let response;

                        if (this.userId) {
                            response = await apiUser.EditUser(
                                payload,
                                this.token,
                                this.userId
                            );
                        } else {
                            response = await apiUser.CreateUser(
                                this.token,
                                payload
                            );
                        }

                        this.$router.push({
                            params: { id: response?.data?.id },
                        });

                        toast.success(
                            this.userId
                                ? this.$t('admin.userForm.saveSuccessEdit')
                                : this.$t('admin.userForm.saveSuccessCreate'),
                            { persist: true }
                        );
                    } else {
                        toast.error(this.$t('admin.userForm.formErrors'));
                    }
                } catch (error) {
                    const message = error.response?.data?.message
                        || this.$t('admin.userForm.saveError');
                    toast.error(message);
                } finally {
                    this.isLoading = false;
                }
            },
            async confirmDelete() {
                try {
                    this.isLoading = true;
                    await apiUser.EditUser(
                        {
                            ...this.dataUserFetched,
                            status: 9,
                            id: undefined,
                            photoFinal: undefined,
                            dateInsert: undefined,
                            orderStatus: undefined,
                        },
                        this.token,
                        this.dataUserFetched.id
                    );
                    toast.success(this.$t('admin.users.deleteSuccess'), {
                        persist: true,
                    });
                    this.$router.push({ path: '/dashboard/usuarios' });
                    
                } catch (error) {
                    toast.error(this.$t('admin.users.deleteError'));
                } finally {
                    this.isDeleteDialogVisible = false;
                    this.isLoading = false;
                    this.dataUserFetched = { name: '', email: '' };
                }
            },
        },
    };
</script>

<style scoped>
    .user-photo {
        display: table;
        width: 100px;
        height: 100px;
        border-radius: 50%;
        object-fit: cover;
        margin: auto;
    }

    .gray-scale {
        filter: grayscale(100%);
    }

    @media screen and (min-width: 768px) {
        .user-photo {
            margin: 0;
        }
    }

    .reset-password-btn:hover {
        background-color: rgb(var(--v-theme-primary)) !important;
        color: white !important;
    }
</style>

<style>
    /* Modo escuro: «Resetar senha» (outlined) e contraste no rodapé do formulário */
    :is(.dashboard.dark, #app.dark-theme) .user-form .reset-password-btn.v-btn--variant-outlined {
        color: #ffffff !important;
        border-color: rgba(255, 255, 255, 0.92) !important;
    }

    :is(.dashboard.dark, #app.dark-theme) .user-form .reset-password-btn.v-btn--variant-outlined .v-btn__content {
        color: #ffffff !important;
    }

    :is(.dashboard.dark, #app.dark-theme) .user-form .reset-password-btn.v-btn--variant-outlined:hover {
        border-color: #ffffff !important;
        background-color: rgba(255, 255, 255, 0.08) !important;
        color: #ffffff !important;
    }

    :is(.dashboard.dark, #app.dark-theme) .user-form .reset-password-btn.v-btn--variant-outlined:hover .v-btn__content {
        color: #ffffff !important;
    }
</style>
